import {
  deleteFileRequest,
  fetchFileUsageRequest,
  fetchFilesRequest,
  updateFileRequest,
  uploadFileRequest,
} from '@/services/api/files';
import { FileKind, FileModel, FileUsageModel } from '@/typings/api/files';
import { processAPIResponseNow } from '@/utils/request';
import { useCallback, useEffect, useRef, useState } from 'react';

export type FileLibraryFilters = {
  kind?: FileKind;
  search?: string;
};

const PAGE_SIZE = 24;

/**
 * The partner's files, loaded a page at a time, plus the four things anyone
 * does with one.
 *
 * Local state rather than a global model: the same hook backs the file manager
 * page and a picker opened from inside an editor, and those two must be able to
 * hold different filters at the same time without fighting over one store.
 */
export function useFileLibrary(initial: FileLibraryFilters = {}) {
  const [items, setItems] = useState<FileModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FileLibraryFilters>(initial);
  /**
   * Bumped to ask for the same page again — reopening a picker should show
   * files uploaded since it was last closed, and "same filters" is exactly when
   * the effect below would otherwise decide there is nothing to do.
   */
  const [refreshToken, setRefreshToken] = useState(0);

  /**
   * Which load is the current one. Typing in the search box fires several
   * overlapping requests and they do not necessarily come back in order; a
   * stale one must not be allowed to overwrite the newest result.
   */
  const requestId = useRef(0);

  const load = useCallback(
    (nextPage = page, nextFilters = filters) => {
      const id = ++requestId.current;
      setLoading(true);

      return fetchFilesRequest({
        query: {
          kind: nextFilters.kind,
          search: nextFilters.search || undefined,
          page: nextPage,
          page_size: PAGE_SIZE,
        },
      }).then((result) => {
        if (id !== requestId.current) return;
        setLoading(false);

        processAPIResponseNow(
          result as any,
          {
            onSuccess: (data) => {
              setItems(data?.data ?? []);
              setTotal(data?.total ?? 0);
            },
          },
          'data',
        );
      });
    },
    [page, filters],
  );

  useEffect(() => {
    void load(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.kind, filters.search, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  const applyFilters = useCallback((next: FileLibraryFilters) => {
    // A filtered list starts at its own beginning; staying on page 4 of the
    // previous result usually lands on an empty page.
    setPage(1);
    setFilters((current) => ({ ...current, ...next }));
  }, []);

  /** Resolves to the stored file, or null when the upload was refused. */
  const upload = useCallback(
    async (
      file: File | Blob,
      options: { context?: string; description?: string } = {},
    ): Promise<FileModel | null> => {
      setUploading(true);
      const result = await uploadFileRequest({ file, ...options });
      setUploading(false);

      let uploaded: FileModel | null = null;
      processAPIResponseNow(
        result as any,
        {
          showErrorMessage: true,
          onSuccess: (data) => {
            uploaded = data as FileModel;
          },
        },
        'data',
      );

      if (uploaded) {
        // Show it without a round trip: it is the newest file, and the list is
        // newest-first.
        setItems((current) => [uploaded as FileModel, ...current]);
        setTotal((current) => current + 1);
      }

      return uploaded;
    },
    [],
  );

  const rename = useCallback(
    async (file: FileModel, name: string, description?: string | null) => {
      const result = await updateFileRequest({
        params: { fileId: file.id },
        payload: { name, description: description ?? undefined },
      });

      processAPIResponseNow(
        result as any,
        {
          showErrorMessage: true,
          onSuccess: (data) => {
            setItems((current) =>
              current.map((item) =>
                item.id === file.id ? (data as FileModel) : item,
              ),
            );
          },
        },
        'data',
      );
    },
    [],
  );

  const usage = useCallback(
    async (file: FileModel): Promise<FileUsageModel> => {
      const result = await fetchFileUsageRequest({
        params: { fileId: file.id },
      });

      let value: FileUsageModel = { in_use: false, usages: [] };
      processAPIResponseNow(
        result as any,
        {
          onSuccess: (data) => {
            if (data) value = data as FileUsageModel;
          },
        },
        'data',
      );

      return value;
    },
    [],
  );

  /**
   * Delete, `force` overriding the server's refusal to remove a file something
   * still references. The caller has already shown those references
   * ({@link useFileDeletion}) — this is not the place that decides.
   *
   * A 409 that reaches here anyway (the pre-flight said "unused", something
   * started using it in between) carries its own explanation, so it is shown
   * rather than replaced with a generic message.
   */
  const remove = useCallback(
    async (file: FileModel, force = false): Promise<boolean> => {
      const result = await deleteFileRequest({
        params: { fileId: file.id },
        force,
      });

      let deleted = false;
      processAPIResponseNow(result as any, {
        showErrorMessage: true,
        onSuccess: () => {
          deleted = true;
        },
      });

      if (deleted) {
        setItems((current) => current.filter((item) => item.id !== file.id));
        setTotal((current) => Math.max(0, current - 1));
      }

      return deleted;
    },
    [],
  );

  return {
    items,
    total,
    page,
    pageSize: PAGE_SIZE,
    setPage,
    filters,
    applyFilters,
    loading,
    uploading,
    refresh,
    upload,
    rename,
    remove,
    usage,
  };
}
